import type { ItemFieldRow } from './item-field-reader';
import type { IntegerRollupDependencies } from './integer-layer-rollup';
import { createIntegerRollupCompiler } from './integer-layer-rollup';
import type { CreatureEssenceRollupDependencies } from './creature-essence-rollup';
import { createCreatureEssenceRollupWithSourceCleanup } from './creature-essence-rollup';
import type { DynamicIntegerSourceDependencies } from './dynamic-integer-sources';
import { createProvisionalAttackCompiler } from './dynamic-integer-sources';
import type { ComputeSourceCleanupDependencies } from './compute-source-cleanup';
import { createComputeSourceCleaner } from './compute-source-cleanup';
import type { RollupCacheMetadata } from './rollup-cache-context';
import { createRollupCacheContext } from './rollup-cache-context';
import { integerCompileWorkingRow, integerCompileResult, putIntegerCompilePath, withIntegerCompileCopyScope } from './integer-compute-slots';
import { leanVitality } from './vitality-build';
import { phpJsonTruthy, phpJsonString, comparePhpStrings } from './php-json-values';
import { createFormulaReadContext } from './formula-read-context';
import { createInventoryTemplateIdentityResolver } from './inventory-template-identity';

type Row = Record<string, unknown>;
const arrayLike = (v: unknown): v is ItemFieldRow => v !== null && typeof v === 'object';
const own = (v: unknown, key: string): unknown => arrayLike(v) && Object.prototype.hasOwnProperty.call(v, key) ? (v as Row)[key] : undefined;
const path = (v: unknown, ...keys: string[]): unknown => keys.reduce(own, v);
const copy = (v: ItemFieldRow): ItemFieldRow => integerCompileResult(integerCompileWorkingRow(v));
/** PHP max() uses regular comparison and retains the first equal value/type. */
function compare(a: unknown, b: unknown): number {
    if (a === b) return 0;
    if (typeof a === 'boolean' || typeof b === 'boolean') return Number(phpJsonTruthy(a)) - Number(phpJsonTruthy(b));
    if (a == null || b == null) {
        if (typeof a === 'string' || typeof b === 'string') return comparePhpStrings(phpJsonString(a), phpJsonString(b));
        return Number(phpJsonTruthy(a)) - Number(phpJsonTruthy(b));
    }
    if (arrayLike(a) || arrayLike(b)) {
        if (!arrayLike(a)) return -1; if (!arrayLike(b)) return 1;
        const entries = Object.entries(a), other = Object.entries(b);
        if (entries.length !== other.length) return entries.length - other.length;
        for (const [key, value] of entries) {
            if (!Object.prototype.hasOwnProperty.call(b, key)) return 1;
            const result = compare(value, own(b, key)); if (result !== 0) return result;
        }
        return 0;
    }
    if (typeof a === 'string' && typeof b === 'string') return comparePhpStrings(a, b);
    if (typeof a === 'number' && typeof b === 'number') return a < b ? -1 : 1;
    const numeric = /^[\t\n\r\v\f ]*[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?[\t\n\r\v\f ]*$/i;
    const text = typeof a === 'string' ? a : b as string;
    if (numeric.test(text)) { const x = Number(a), y = Number(b); return x < y ? -1 : x > y ? 1 : 0; }
    // A nonnumeric string compares lexically against the number's PHP spelling.
    const x = Array.from(phpJsonString(a)), y = Array.from(phpJsonString(b));
    for (let i = 0; i < Math.min(x.length, y.length); i++) { const d = x[i]!.codePointAt(0)! - y[i]!.codePointAt(0)!; if (d) return d; }
    return x.length - y.length;
}
export type CreatureIntegerRollupDependencies = IntegerRollupDependencies & CreatureEssenceRollupDependencies
    & DynamicIntegerSourceDependencies & ComputeSourceCleanupDependencies & RollupCacheMetadata
    & {allEssences: readonly {variable_name: string; item_id: string}[]};

/** Native integer/essence compilation. NPC definition finalization requires
 * the optional native signature/output producers even on its first compile.
 */
export function createCreatureIntegerRollupCompiler(deps: CreatureIntegerRollupDependencies, initialCarry: ItemFieldRow = {}) {
    if (!deps.resolveInventoryIdentity && deps.queryTemplateDefinition) deps = {...deps, resolveInventoryIdentity: createInventoryTemplateIdentityResolver(deps.queryTemplateDefinition)};
    const integers = createIntegerRollupCompiler(deps), cleaner = createComputeSourceCleaner(deps);
    const essences = createCreatureEssenceRollupWithSourceCleanup(deps, cleaner), attacks = createProvisionalAttackCompiler(deps, cleaner);
    const cache = createRollupCacheContext(deps, initialCarry), readers = createFormulaReadContext(deps);
    // NPC initial callers and historical replays opt into cache producers.
    return (input: ItemFieldRow, cached = false): {row: ItemFieldRow; skipped: boolean} => withIntegerCompileCopyScope(() => {
        if (!arrayLike(input)) throw new TypeError('Outer rollup requires a PHP array');
        let row = copy(input), signature: string | null = null, carry: ItemFieldRow | null = null, skipped = false;
        const put = (keys: string[], value: unknown): void => { const working = integerCompileWorkingRow(row); putIntegerCompilePath(working, keys, value); row = integerCompileResult(working); };
        if (cached) {
            signature = cache.signature(row); put(['computed', '_cache', 'rollup_cluster_sig'], signature);
            carry = cache.takeCarry(cache.key(row));
            if (carry !== null && own(carry, 'sig') === signature && arrayLike(own(carry, 'out'))) {
                row = cache.reproduceUnsets(cache.restore(row, own(carry, 'out') as ItemFieldRow));
                put(['computed', '_cache', 'rollup_cluster_out'], own(carry, 'out')); skipped = true;
            }
        }
        if (!skipped) {
            row = integers.compile(row);
            for (const essence of deps.allEssences) if (essence.variable_name !== 'luck') row = essences(row, essence.variable_name, essence.item_id, essence.variable_name === 'fortune' ? 2 : 0);
            if (path(row, 'encounter_creatures', 'encounter_creature_id') == null) {
                put(['computed', 'vitality', 'v_current'], path(row, 'computed', 'vitality', 'v_max') ?? 0);
                for (const [key, value] of Object.entries({v_percent: 100, v_bar_v_percent: 100, v_bar_v_temp_percent: 0})) put(['computed', 'vitality', key], value);
                put(['computed', 'vitality'], leanVitality(path(row, 'computed', 'vitality') as ItemFieldRow));
                if (!phpJsonTruthy(path(row, 'computed', 'vitality', 'v_max'))) {
                    const id = readers.itemField(row, 'item_id') ?? readers.itemField(own(row, 'player_characters') as ItemFieldRow ?? null, 'item_id') ?? 'unknown';
                    deps.trace?.(['rollup-warning', `rollup_integer_variables: encounter_creature_id AND v_max NOT SET — item_id: ${phpJsonString(id)}`]);
                    for (const key of ['v_max', 'v_current', 'v_percent', 'v_bar_v_percent', 'v_bar_v_temp_percent']) put(['computed', 'vitality', key], 0);
                }
            }
            if (phpJsonTruthy(path(row, 'encounter_creatures', 'encounter_creature_id')) && path(row, 'encounter_creatures', 'weapon_attack') == null) put(['encounter_creatures', 'weapon_attack'], 0);
            row = attacks(row);
            const working = integerCompileWorkingRow(row);
            for (const key of deps.damagePercentKeys) {
                const values = own(working, key);
                if (!arrayLike(values) || !Object.keys(values).length) continue;
                const entries = Object.values(values); let max = entries[0];
                for (const entry of entries.slice(1)) if (compare(max, entry) < 0) max = entry;
                working[key] = [max];
            }
            row = integerCompileResult(working);
            if (cached) put(['computed', '_cache', 'rollup_cluster_out'], cache.snapshot(row));
        }
        return {row, skipped};
    });
}
